"use client";

import { useCallback, type MouseEvent } from "react";

import {
  appendLeadAttribution,
  createCtaEventId,
  getOrCreateLeadRef,
  getSessionInboundSourceCode,
} from "@/lib/cta-analytics";
import { trackJourneyEvent } from "@/lib/journey-analytics";
import { sendMetaTrackPayload } from "@/lib/meta-track-client";
import { trackQuizEvent } from "@/lib/quiz-analytics";
import type { Locale } from "@/lib/i18n/locales";
import {
  quizResultsCopy,
  buildQuizWhatsAppMessage,
} from "@/lib/i18n/quiz-results-copy";

type QuizWhatsAppCtaProps = {
  locale?: Locale;
  quizId: string;
  quizTitle: string;
  score: number;
  total: number;
  /** Human-readable names of missed topics for the WhatsApp message. */
  missedTopics?: string[];
};

const WA_NUMBER = "6590065997";

export default function QuizWhatsAppCta({
  locale = "en",
  quizId,
  quizTitle,
  score,
  total,
  missedTopics,
}: QuizWhatsAppCtaProps) {
  const copy = quizResultsCopy(locale);
  const message = buildQuizWhatsAppMessage(
    quizTitle,
    score,
    total,
    missedTopics,
    locale
  );
  const href = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      const eventId = createCtaEventId();
      const leadRef = getOrCreateLeadRef();
      const inboundSourceCode = getSessionInboundSourceCode();
      event.currentTarget.href = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
        appendLeadAttribution(message, leadRef, inboundSourceCode)
      )}`;

      trackJourneyEvent("whatsapp_click", {
        commercial_intent: "direct",
        cta_family: "lead_cta",
        event_id: eventId,
        ...(inboundSourceCode
          ? { inbound_source_code: inboundSourceCode }
          : {}),
        is_primary_cta: true,
        journey_name: "quiz_whatsapp_lead",
        lead_ref: leadRef,
        source_surface: "quiz_results",
        target_id: "whatsapp_consult",
        target_stage: "lead_intent",
        target_type: "channel",
      });

      trackQuizEvent("quiz_whatsapp_lead", {
        quizId,
        score,
        total,
      });

      // Meta Pixel + CAPI (reuse WhatsAppButton's pattern)
      if (typeof window !== "undefined") {
        if (typeof window.fbq === "function") {
          window.fbq("track", "Contact", {}, { eventID: eventId });
        }

        const getCookie = (name: string) => {
          const pair = document.cookie
            .split("; ")
            .find((row) => row.startsWith(`${name}=`));
          if (!pair) return undefined;
          const [, value] = pair.split("=");
          return value ? decodeURIComponent(value) : undefined;
        };

        const payload = JSON.stringify({
          event_name: "Contact",
          event_id: eventId,
          event_source_url: window.location.href,
          fbp: getCookie("_fbp"),
          fbc: getCookie("_fbc"),
        });

        sendMetaTrackPayload(payload);
      }
    },
    [message, quizId, score, total]
  );

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      data-nosnippet
      data-ga-event="whatsapp_click"
      data-ga-manual="true"
      data-ga-impression="true"
      data-ga-category="CTA"
      data-ga-journey="quiz_whatsapp_lead"
      data-ga-source-surface="quiz_results"
      data-ga-target-stage="lead_intent"
      data-ga-target-type="channel"
      data-ga-target-id="whatsapp_consult"
      data-ga-cta-family="lead_cta"
      data-ga-commercial-intent="direct"
      data-ga-primary="true"
      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1da851]"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
      {copy.cta}
    </a>
  );
}
